import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import _debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { Typography } from 'src/components-v2/DataDisplay';
import { Box, Column, Stack } from 'src/components-v2/Layout';
import Wrapper from 'src/containers/Wrapper/Wrapper';
import styled from 'threads5/themes/styled';
import type {
  ICareersSearchConfigData,
  ICareersSearchJob,
  ICareersSearchProps,
} from './CareersSearch.types';
import { getSearchWords, matchesSearchWords } from './CareersSearch.utils';
import SearchFilter from './CareersSearchFilter';
import CareersSearchItem from './CareersSearchItem';
import SelectFilter from './CareersSelectFilter';

// Used to "jump" to the jobs section if a URL is shared.
const COMPONENT_ID = 'search';

/**
 * Simple helper to return search parameters from the URL - if they exist
 */
const getFiltersFromSearch = (jobsData?: ICareersSearchJob[]) => {
  if (typeof window === 'undefined') {
    return { search: '', department: '', location: '' };
  }
  const search = new URLSearchParams(window.location.search);
  const filters = {
    search: search.get('search') ?? '',
    department: search.get('department') ?? '',
    location: search.get('location') ?? '',
  };

  // if jobs data is passed, validate what we can
  if (jobsData && jobsData.length > 0) {
    // validate the department
    if (filters.department) {
      if (
        !jobsData.some((job) => {
          return job.department === filters.department;
        })
      ) {
        filters.department = '';
      }
    }

    // validate the location
    if (filters.location) {
      if (
        !jobsData.some((job) => {
          return job.locations.includes(filters.location);
        })
      ) {
        filters.location = '';
      }
    }
  }

  return filters;
};

const updateUrlSearch = _debounce((filters) => {
  if (typeof window !== 'undefined') {
    const search = new URLSearchParams(window.location.search);
    const searchText = filters.search.trim();

    // search filter
    if (searchText) {
      search.set('search', searchText);
    } else {
      search.delete('search');
    }

    // department filter
    if (filters.department) {
      search.set('department', filters.department);
    } else {
      search.delete('department');
    }

    // location filter
    if (filters.location) {
      search.set('location', filters.location);
    } else {
      search.delete('location');
    }

    // update the window history state
    const newUrl = `${window.location.pathname}${
      search.size > 0 ? `?${search.toString()}#${COMPONENT_ID}` : ''
    }`;
    window.history.replaceState({}, '', newUrl);
  }
}, 250);

const NO_JOBS: ICareersSearchJob[] = [];

const CareersSearch = ({ fields, sys }: ICareersSearchProps) => {
  const { config, title } = fields;
  const hasJobsData = Boolean(fields.jobsData);
  const jobsData = fields.jobsData ?? NO_JOBS;
  const configJson = config?.fields?.data;
  const [resultCount, setResultCount] = useState(jobsData.length);
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    location: '',
  });

  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  const [allDepartments, allLocations] = useMemo(() => {
    const departments = new Set<string>();
    const locations = new Set<string>();

    // accumulate unique departments and locations
    jobsData.forEach((job) => {
      departments.add(job.department);
      job.locations.forEach((location) => {
        locations.add(location);
      });
    });

    return [Array.from(departments), Array.from(locations)];
  }, [jobsData]);

  const jobsByDepartment = useMemo(() => {
    const searchWords = getSearchWords(filters.search);

    // get jobs results
    const results = jobsData.reduce<{ [key: string]: ICareersSearchJob[] }>(
      (acc, job) => {
        // if a department is selected, filter jobs by it
        if (filters.department) {
          if (filters.department !== job.department) {
            return acc;
          }
        }

        // if a location is selected, filter jobs by it
        if (filters.location) {
          if (!job.locations.includes(filters.location)) {
            return acc;
          }
        }

        if (!matchesSearchWords(job.title, searchWords)) {
          return acc;
        }

        // if we're going to add a job, make sure the department exists
        // this will only add departments that actually have at least one job
        if (!acc[job.department]) {
          acc[job.department] = [];
        }
        acc[job.department].push(job);
        return acc;
      },
      {},
    );

    // if we have no results, set the state
    setResultCount(Object.keys(results).length);

    // upate the URL params if we have filters
    updateUrlSearch(filters);

    return results;
  }, [jobsData, filters]);

  // effect to setup a browser history state listener
  // this will update search filters for browser history navigation
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      // get possible filters from URL params
      const search = getFiltersFromSearch(jobsData);

      // only do an update to the filters if there is at least one search set (limits re-renders)
      if (search.search || search.department || search.location) {
        setFilters({
          search: search.search,
          department: search.department,
          location: search.location,
        });
      }
    };

    // run this once initally on load to update filters
    handlePopState();

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [jobsData]);

  // if we dont have any jobs data - dont display anything for now
  if (!hasJobsData) {
    return null;
  }

  return (
    <Wrapper
      id={COMPONENT_ID}
      component='section'
      {...inspectorProps({ fieldId: 'internalTitle' })}
    >
      <Column xs={24} sm={24} lg={20} lgOffset={2}>
        <Stack gap={{ xs: 8, md: 10 }} useFlexGap>
          <Stack gap={5} useFlexGap>
            {title && (
              <Typography component='h2' variant='h2'>
                {title}
              </Typography>
            )}
            <FilterContainer>
              <SearchFilter
                value={filters.search}
                onChange={(event) => {
                  setFilters((f) => {
                    return { ...f, search: event.target.value };
                  });
                }}
              />
              <SelectFilter
                label='Teams'
                items={allDepartments}
                value={filters.department}
                onChange={(event) => {
                  setFilters((f) => {
                    return { ...f, department: event.target.value };
                  });
                }}
              />
              <SelectFilter
                label='Locations'
                items={allLocations}
                value={filters.location}
                onChange={(event) => {
                  setFilters((f) => {
                    return { ...f, location: event.target.value };
                  });
                }}
              />
            </FilterContainer>
          </Stack>
          <Stack gap={4}>
            {resultCount < 1 ? (
              <NoResults />
            ) : (
              Object.entries(jobsByDepartment).map(([department, jobs]) => {
                return (
                  <Department
                    key={department}
                    department={department}
                    jobs={jobs}
                    config={configJson}
                  />
                );
              })
            )}
          </Stack>
        </Stack>
      </Column>
    </Wrapper>
  );
};

const NoResults = () => {
  return (
    <Stack gap={2}>
      <Typography component='h3' variant='h3'>
        Oops!
      </Typography>
      <Typography component='p' variant='p' sx={{ color: 'base/black/800' }}>
        We're sorry, your search didn't return any results. Adjust your search
        criteria and try again.
      </Typography>
    </Stack>
  );
};

const Department = ({
  department,
  jobs,
  config,
}: {
  department: string;
  jobs: ICareersSearchJob[];
  config?: ICareersSearchConfigData;
}) => {
  const departmentDesc = config?.departments?.[department]?.description || '';

  return (
    <Stack gap={4}>
      <Stack gap={1.25}>
        <Typography component='h3' variant='h3'>
          {department}
        </Typography>
        {departmentDesc && (
          <Typography
            component='p'
            variant='p'
            sx={{ color: 'base/black/800' }}
          >
            {departmentDesc}
          </Typography>
        )}
      </Stack>
      <Stack gap={3} component={'ul'} sx={{ ml: 0 }}>
        {jobs.map((job) => {
          return (
            <Box key={job.key} component={'li'} sx={{ listStyle: 'none' }}>
              <CareersSearchItem job={job} />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

const FilterContainer = styled('div')(({ theme }) => {
  return {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: theme.spacing(4),
    },
  };
});

export default CareersSearch;
